import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// Absolute so requests reach this app's own backend even when federated into
// another origin (e.g. rendered inside erp.dragnet.ng) — a relative path would
// resolve against whichever host page is currently loaded.
const BASE = `${import.meta.env.VITE_API_URL as string}/api`;

/** An API failure. `errors` carries per-field messages when the server validated the request (HTTP 400). */
export interface ApiRequestError extends Error {
  status?: number;
  errors?: Record<string, string>;
}

async function doFetch(path: string, options: RequestInit): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  // The erp_auth cookie carries auth in both standalone and federated mode — it's
  // same-site across every *.dragnet.ng subdomain — so no Authorization header is needed.
  return fetch(`${BASE}${path}`, { ...options, headers, credentials: 'include' });
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    const failure = new Error((err as { message?: string }).message ?? `Request failed (${res.status})`) as ApiRequestError;
    failure.status = res.status;
    failure.errors = (err as { errors?: Record<string, string> }).errors;
    throw failure;
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export function useApiClient() {
  const { refreshToken } = useAuth();

  const authed = useCallback(
    async <T>(path: string, options: RequestInit = {}): Promise<T> => {
      const res = await doFetch(path, options);

      if (res.status === 401) {
        // Session expired or was revoked — no way to refresh a cookie-based session
        // client-side, so this clears it and redirects to the ERP shell.
        await refreshToken();
      }

      return parseResponse<T>(res);
    },
    [refreshToken],
  );

  return {
    get:    <T>(path: string)                  => authed<T>(path),
    post:   <T>(path: string, body: unknown)   => authed<T>(path, { method: 'POST',   body: JSON.stringify(body) }),
    patch:  <T>(path: string, body: unknown)   => authed<T>(path, { method: 'PATCH',  body: JSON.stringify(body) }),
    put:    <T>(path: string, body: unknown)   => authed<T>(path, { method: 'PUT',    body: JSON.stringify(body) }),
    delete: <T>(path: string)                  => authed<T>(path, { method: 'DELETE' }),

    /** Unauthenticated endpoints — email-link pages */
    publicGet:   <T>(path: string)               => doFetch(path, {}).then(r => parseResponse<T>(r)),
    publicPost:  <T>(path: string, body: unknown) => doFetch(path, { method: 'POST',  body: JSON.stringify(body) }).then(r => parseResponse<T>(r)),
    publicPatch: <T>(path: string, body: unknown) => doFetch(path, { method: 'PATCH', body: JSON.stringify(body) }).then(r => parseResponse<T>(r)),
  };
}
